import { Button } from "@/common/button";
import { EButtonType } from "@/common/button/button-models";
import { StatusScreen } from "@/common/status-screen";
import { isChunkLoadError } from "@/utils/lazy";
import { renderIf } from "@/utils/react-conditional";
import { sendWarningMessage } from "@/utils/slack";
import React from "react";

// Slack user ID (not a secret) tagged on real UI crashes so they notify.
const CRASH_ALERT_SLACK_USER_ID = "UDZB04NVA";

export interface IErrorCatcherProps {
  customError?: React.ReactNode;
  showHomeLink?: boolean;
  children: React.ReactNode;
}

export interface IErrorCatcherState {
  isError: boolean;
  isNetworkError: boolean;
}

// The whole-page fallback shown when something crashed.
export const ErrorScreen = React.memo(function ErrorScreen(props: {
  isNetworkError: boolean;
  showHomeLink?: boolean;
}) {
  return (
    <StatusScreen
      fullScreen
      emoji={props.isNetworkError ? "🔌" : "🔧"}
      title={
        props.isNetworkError ? "Something didn’t load" : "Something went wrong"
      }
      subtitle={
        props.isNetworkError
          ? "We couldn't load part of the page, most likely a temporary network issue. Check your connection and try reloading."
          : "Our team has been notified and we're on it. In the meantime, try reloading the page."
      }
    >
      <Button
        buttonType={EButtonType.BlueWide}
        onClick={() => window.location.reload()}
      >
        {props.isNetworkError ? "Try reload" : "Reload page"}
      </Button>
      {props.showHomeLink && (
        <Button buttonType={EButtonType.GhostWide} href="/" target="_self">
          Go to homepage
        </Button>
      )}
    </StatusScreen>
  );
});

export class ErrorCatcher extends React.PureComponent<
  IErrorCatcherProps,
  IErrorCatcherState
> {
  constructor(props: IErrorCatcherProps) {
    super(props);
    this.state = {
      isError: false,
      isNetworkError: false,
    };
  }

  // catch and report React UI errors
  public componentDidCatch(error: Error, info: React.ErrorInfo) {
    // A chunk/CSS that failed to load even after lazy.ts's retries is
    // almost always a transient network or mid-deploy hiccup, not a bug in
    // our code - say so instead of implying our team broke something (see
    // #web-logs crash-bot discussion). Still reported to Slack so we keep
    // visibility if it becomes chronic, just tagged so it's easy to
    // distinguish from actual app crashes at a glance.
    const isNetworkError = isChunkLoadError(error);
    this.setState({ isError: true, isNetworkError }); // display fallback UI
    if (import.meta.env.PROD) {
      if (isNetworkError) {
        // Short one-liner: these are noisy after every deploy and the stack
        // adds nothing, so they must not look like a real crash.
        sendWarningMessage(
          `chunk load failed for \`${window.username}\` on \`${
            window.location.href
          }\`: ${error}`,
          undefined,
          "chunk-load-bot",
          ":satellite:"
        );
      } else {
        sendWarningMessage(
          `:boom: *UI CRASH* :boom: <@${CRASH_ALERT_SLACK_USER_ID}>\n*User:* \`${
            window.username
          }\`\n*URL:* \`${window.location.href}\`\n*${error.name}:* \`${error.message}\`\n*Stack:*\n\`\`\`\n${JSON.stringify(
            info
          )}\n\`\`\`\n*User agent:* \`${navigator.userAgent}\``,
          undefined,
          "UI-CRASH-bot",
          ":boom:"
        );
      }
    }
  }

  private renderError = () => {
    if (this.props.customError) {
      return this.props.customError;
    }
    return (
      <ErrorScreen
        isNetworkError={this.state.isNetworkError}
        showHomeLink={this.props.showHomeLink}
      />
    );
  };

  public render() {
    return (
      <>
        {renderIf(this.state.isError, {
          ifTrue: () => this.renderError(),
          ifFalse: () => this.props.children,
        })}
      </>
    );
  }
}
